import type { CaptureData } from '@/components/StepCapture'
import type { ElementKey } from '@/lib/experience'
import PageProtocol from '@/components/PageProtocol'
import StepAudio from '@/components/StepAudio'
import StepBalanced from '@/components/StepBalanced'
import StepCapture from '@/components/StepCapture'
import StepOffers from '@/components/StepOffers'
import StepPain from '@/components/StepPain'
import StepQuestions from '@/components/StepQuestions'
import StepRevelation from '@/components/StepRevelation'
import StepTrindade from '@/components/StepTrindade'
import {
  computeDominant,
  ELEMENT_SITUATIONS,
  ELEMENT_TRINDADE,
  ELEMENTS,
} from '@/lib/experience'
import { AnimatePresence, motion } from 'framer-motion'
import { useState } from 'react'

type Phase =
  | 'capture'
  | 'questions'
  | 'revelation'
  | 'pain'
  | 'trindade'
  | 'audio'
  | 'offers'
  | 'protocol'
  | 'balanced'

// ─── Barra de progresso de etapas (Etapas 2–7) ────────────────────────────────
const PHASE_STEP: Partial<Record<Phase, number>> = {
  questions:  2,
  revelation: 3,
  pain:       4,
  trindade:   5,
  audio:      6,
  offers:     7,
}

function JourneyProgress({ phase, accent }: { phase: Phase; accent: string }) {
  const step = PHASE_STEP[phase]
  if (!step) return null
  const total = 7
  const pct = ((step - 1) / (total - 1)) * 100

  return (
    <motion.div
      key={phase}
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="w-full px-4 sm:px-5 md:px-10 pb-3"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-1.5 gap-2">
          <span
            className="text-[9px] sm:text-[10px] tracking-[0.3em] uppercase font-body"
            style={{ color: `${accent}99` }}
          >
            Etapa <span className="numerals">{step}</span> de <span className="numerals">{total}</span>
          </span>
          <span
            className="text-[9px] sm:text-[10px] tracking-[0.25em] uppercase font-body"
            style={{ color: `${accent}55` }}
          >
            Jornada
          </span>
        </div>
        <div
          className="h-[1px] w-full rounded-full overflow-hidden"
          style={{ background: `${accent}18` }}
        >
          <motion.div
            className="h-full rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            style={{
              background: `linear-gradient(90deg, ${accent}66, ${accent}cc)`,
            }}
          />
        </div>
      </div>
    </motion.div>
  )
}


const AMBIENT_PHASES: Phase[] = ['revelation', 'pain', 'trindade', 'audio', 'offers', 'protocol']

const SAVE_LEAD_URL = 'https://api.lumi.new/v1/functions/p440726801590865920/saveLead'

function Home() {
  const [phase, setPhase] = useState<Phase>('capture')
  const [capture, setCapture] = useState<CaptureData | null>(null)
  const [dominant, setDominant] = useState<ElementKey | null>(null)
  const [painIndex, setPainIndex] = useState<number | null>(null)

  const ambientClass =
    dominant && AMBIENT_PHASES.includes(phase)
      ? ELEMENTS[dominant].ambientClass
      : 'bg-ambient'

  const advance = (next: Phase) => {
    setPhase(next)
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const restart = () => {
    setCapture(null)
    setDominant(null)
    setPainIndex(null)
    setPhase('capture')
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  /**
   * Called once pain is selected (end of Step 4).
   * Saves lead + fires the immediate email via Deno Function (fire-and-forget).
   */
  const handlePainSelect = (element: ElementKey, index: number) => {
    setPainIndex(index)
    advance('trindade')

    if (!capture) return

    const situationText = ELEMENT_SITUATIONS[element]?.[index] ?? ''
    const trindadeText  = ELEMENT_TRINDADE[element]?.[index]   ?? ''

    // Fire-and-forget — don't block the UX
    fetch(SAVE_LEAD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: capture.name,
        email: capture.email,
        whatsapp: capture.whatsapp,
        dominantElement: element,
        situationIndex: index,
        situationText,
        trindadeText,
      }),
    }).catch(() => {
      // Silently ignore — user experience is not blocked
    })
  }

  return (
    <div className={`min-h-screen ${ambientClass} transition-[background] duration-1000`}>
      {/* Top brand strip */}
      {/* Barra de progresso de etapas */}
      {dominant
        ? <JourneyProgress phase={phase} accent={ELEMENTS[dominant].accentSoft} />
        : <JourneyProgress phase={phase} accent="#ece4d1" />
      }

      <header className="w-full px-4 sm:px-5 md:px-10 pt-5 sm:pt-6 pb-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-[var(--ink-soft)] min-w-0">
            <span className="font-display italic text-[20px] sm:text-[22px] leading-none text-[var(--ink)]">Reative</span>
            <span className="text-[9px] sm:text-[10px] tracking-[0.25em] sm:tracking-[0.3em] uppercase font-body pt-1 truncate">os <span className="numerals">4</span> Elementos</span>
          </div>
          <div className="flex items-center gap-1.5 opacity-60 flex-shrink-0">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: ELEMENTS.terra.accentSoft }} />
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: ELEMENTS.agua.accentSoft }} />
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: ELEMENTS.fogo.accentSoft }} />
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: ELEMENTS.ar.accentSoft }} />
          </div>
        </div>
      </header>

      <main className="px-4 sm:px-5 md:px-10 pb-20 pt-4 sm:pt-6 md:pt-12">
        <AnimatePresence mode="wait">

          {phase === 'capture' && (
            <div key="capture">
              <StepCapture
                initial={capture ?? undefined}
                onSubmit={(data) => {
                  setCapture(data)
                  advance('questions')
                }}
              />
            </div>
          )}

          {phase === 'questions' && (
            <div key="questions">
              <StepQuestions
                onComplete={(answers) => {
                  const result = computeDominant(answers)
                  if (result === null) {
                    // All elements scored 0 — no tiebreaker, show balanced screen.
                    setDominant(null)
                    advance('balanced')
                  } else {
                    setDominant(result)
                    advance('revelation')
                  }
                }}
              />
            </div>
          )}

          {phase === 'revelation' && dominant && (
            <div key="revelation">
              <StepRevelation
                element={dominant}
                onContinue={() => advance('pain')}
              />
            </div>
          )}

          {phase === 'pain' && dominant && (
            <div key="pain">
              <StepPain
                element={dominant}
                onSelect={(i) => handlePainSelect(dominant, i)}
              />
            </div>
          )}

          {phase === 'trindade' && dominant && painIndex !== null && (
            <div key="trindade">
              <StepTrindade
                element={dominant}
                situationIndex={painIndex}
                onContinue={() => advance('audio')}
              />
            </div>
          )}

          {phase === 'audio' && dominant && (
            <div key="audio">
              <StepAudio
                element={dominant}
                onContinue={() => advance('offers')}
              />
            </div>
          )}

          {phase === 'offers' && dominant && (
            <div key="offers">
              <StepOffers
                element={dominant}
                onRestart={restart}
                onProtocol={() => advance('protocol')}
              />
            </div>
          )}

          {phase === 'protocol' && (
            <div key="protocol">
              <PageProtocol onBack={() => advance('offers')} />
            </div>
          )}

          {phase === 'balanced' && (
            <div key="balanced">
              <StepBalanced
                onRestart={restart}
                onProtocol={() => advance('protocol')}
              />
            </div>
          )}

        </AnimatePresence>
      </main>

      <footer className="px-4 sm:px-5 md:px-10 pb-8 pt-4">
        <div className="max-w-6xl mx-auto flex items-center justify-center">
          <p className="text-[9px] sm:text-[10px] tracking-[0.25em] sm:tracking-[0.3em] uppercase text-[var(--ink-soft)] font-body opacity-60 text-center">
            Reative os <span className="numerals">4</span> Elementos · Uma jornada de transformação
          </p>
        </div>
      </footer>
    </div>
  )
}

export default Home
